import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, Star, Award, AlertCircle } from 'lucide-react';
import type { Prize } from '../../lib/supabase';

type Props = {
  entries: Array<{ ticket_number: string }>;
  prizes: Prize[];
  onComplete: (winners: Array<{ ticket_number: string; prize: Prize }>) => void;
  onClose: () => void;
};

const RaffleDrawAnimation: React.FC<Props> = ({ entries, prizes, onComplete, onClose }) => {
  const [currentPrizeIndex, setCurrentPrizeIndex] = useState(0);
  const [winners, setWinners] = useState<Array<{ ticket_number: string; prize: Prize }>>([]);
  const [spinning, setSpinning] = useState(true);
  const [displayedTickets, setDisplayedTickets] = useState<string[]>([]);
  const [finalWinner, setFinalWinner] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    startDrawing();
  }, []);

  const startDrawing = async () => {
    const availableEntries = [...entries];
    const selectedWinners: Array<{ ticket_number: string; prize: Prize }> = [];
    const usedTickets = new Set<string>();

    for (const prize of prizes) {
      if (availableEntries.length === 0) {
        setError('Not enough eligible entries for remaining prizes');
        break;
      }

      setSpinning(true);
      setCurrentPrizeIndex(prizes.indexOf(prize));

      // Filter out entries that have already won
      const eligibleEntries = availableEntries.filter(
        entry => !usedTickets.has(entry.ticket_number)
      );

      if (eligibleEntries.length === 0) {
        setError('No more eligible entries available');
        break;
      }

      await simulateTicketSpin(eligibleEntries);

      // Select winner from eligible entries
      const winnerIndex = Math.floor(Math.random() * eligibleEntries.length);
      const winner = eligibleEntries[winnerIndex];
      
      // Add to winners list and mark ticket as used
      selectedWinners.push({ ticket_number: winner.ticket_number, prize });
      usedTickets.add(winner.ticket_number);

      // Remove winner from available entries
      const originalIndex = availableEntries.findIndex(
        entry => entry.ticket_number === winner.ticket_number
      );
      if (originalIndex !== -1) {
        availableEntries.splice(originalIndex, 1);
      }

      setFinalWinner(winner.ticket_number);
      setSpinning(false);
      setWinners(selectedWinners);

      // Pause before next prize
      await new Promise(resolve => setTimeout(resolve, 3000));
    }

    onComplete(selectedWinners);
  };

  const simulateTicketSpin = async (availableEntries: Array<{ ticket_number: string }>) => {
    const spinDuration = 3000;
    const tickInterval = 50;
    const iterations = spinDuration / tickInterval;

    for (let i = 0; i < iterations; i++) {
      const randomTickets = Array.from({ length: 3 }, () => {
        const randomEntry = availableEntries[Math.floor(Math.random() * availableEntries.length)];
        return randomEntry.ticket_number;
      });
      
      setDisplayedTickets(randomTickets);
      await new Promise(resolve => setTimeout(resolve, tickInterval));
    }
  };

  const getPrizeIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Trophy className="h-8 w-8 text-yellow-500" />;
      case 2:
        return <Award className="h-8 w-8 text-gray-400" />;
      case 3:
        return <Star className="h-8 w-8 text-orange-500" />;
      default:
        return <Star className="h-8 w-8 text-blue-500" />;
    }
  };

  if (error) {
    return (
      <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8 max-w-md text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-4">{error}</h3>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50">
      <div className="max-w-2xl w-full mx-4">
        <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
          <div className="bg-gradient-to-br from-purple-600 to-blue-600 p-8 text-white">
            <h2 className="text-3xl font-bold text-center mb-2">Drawing Winners</h2>
            {prizes[currentPrizeIndex] && (
              <div className="flex items-center justify-center space-x-2">
                {getPrizeIcon(prizes[currentPrizeIndex].rank)}
                <p className="text-xl">
                  {prizes[currentPrizeIndex].rank}{getOrdinalSuffix(prizes[currentPrizeIndex].rank)} Prize
                </p>
              </div>
            )}
          </div>

          <div className="p-8">
            <div className="bg-gray-900 rounded-lg p-6 mb-6">
              <div className="flex justify-center items-center space-x-2">
                {spinning ? (
                  displayedTickets.map((ticket, index) => (
                    <motion.div
                      key={index}
                      className="bg-gray-800 rounded px-4 py-2 text-2xl font-mono text-white"
                      animate={{
                        y: [0, -10, 0],
                        transition: {
                          duration: 0.5,
                          repeat: Infinity,
                          delay: index * 0.1
                        }
                      }}
                    >
                      {ticket}
                    </motion.div>
                  ))
                ) : (
                  <AnimatePresence>
                    <motion.div
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="bg-green-500 rounded px-6 py-3 text-3xl font-mono text-white"
                    >
                      {finalWinner}
                    </motion.div>
                  </AnimatePresence>
                )}
              </div>
            </div>

            <div className="space-y-4">
              {winners.map((winner, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between bg-gray-50 p-4 rounded-lg"
                >
                  <div className="flex items-center">
                    {getPrizeIcon(winner.prize.rank)}
                    <div className="ml-3">
                      <p className="font-semibold">{winner.prize.rank}{getOrdinalSuffix(winner.prize.rank)} Prize</p>
                      <p className="text-sm text-gray-600">{winner.prize.name}</p>
                    </div>
                  </div>
                  <div className="font-mono text-lg">{winner.ticket_number}</div>
                </div>
              ))}
            </div>

            {!spinning && winners.length === prizes.length && (
              <div className="mt-6 text-center">
                <button
                  onClick={onClose}
                  className="px-6 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const getOrdinalSuffix = (n: number): string => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
};

export default RaffleDrawAnimation;